import React, { useEffect, useState, useRef, useCallback } from "react";
import { Post, ZapcapVideoStatus } from "../../types/posts";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomToast from "../../components/misc/CustomToast";
import Calendar from "../../components/calendar/Calendar";
import FloatingProcessingPosts from "../../components/calendar/FloatingProcessingPosts";
import { getAuthHeader } from "../../utils/authHeader";
import LoadingScreen from "../LoadingScreen";
import WelcomeListing from "../../components/listings/WelcomeListing";
import { welcomeOverlayPref } from "../../utils/prefCookie";
import { useSocket } from "../../context/SocketContext";
import { useDispatch } from "react-redux";
import { setProcessingPosts } from "../../store/processingPostsSlice";

// MB-1393: coalescing window for socket-driven refetches, and the longest a
// continuous event stream may defer an update.
const REFETCH_DEBOUNCE_MS = 2000;
const REFETCH_MAX_WAIT_MS = 5000;

type OverlayType =
    | "firstListing"
    | "listingCampaign"
    | "promotionCampaign"
    | "listingPromotionCampaign"
    | null;

const HomeScreen: React.FC = () => {
    const [posts, setPosts] = React.useState<Post[]>([]);
    const { socket, isAuthenticated } = useSocket();
    const dispatch = useDispatch();

    // Whenever the posts feed updates, derive the captioning-in-progress
    // subset (sorted newest scheduled first) and push it into the floating
    // panel. A post is considered "processing" when:
    //   (a) zapCapVideoStatus === "processing" (explicit ZapCap lifecycle field), OR
    //   (b) status === "VideoProcessing" for a Caption Video post
    useEffect(() => {
        const isCaptionVideo = (p: Post) =>
            p.contentType === "Caption Video" || p.type === "Caption Video";

        const processing = (posts ?? [])
            .filter(
                (p) =>
                    p.zapCapVideoStatus?.toLowerCase() === ZapcapVideoStatus.Processing ||
                    (p.status?.toLowerCase() === "videoprocessing" && isCaptionVideo(p))
            )
            .slice()
            .sort(
                (a, b) =>
                    new Date(b.scheduledFor).getTime() -
                    new Date(a.scheduledFor).getTime()
            );
        dispatch(setProcessingPosts(processing));
    }, [posts, dispatch]);

    const [loading, setLoading] = React.useState(false);
    const [hasFetched, setHasFetched] = React.useState(false);
    const refetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const maxWaitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [overlayType, setOverlayType] = useState<OverlayType>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);

    const [counts, setCounts] = React.useState({
        listingCount: 0,
        listingCampaignCount: 0,
        promotionCount: 0,
        promotionCampaignCount: 0,
    });

    const welcome: boolean = sessionStorage.getItem("welcome") === "true";

    const params = new URLSearchParams(window.location.search);
    const postType: string | null = params.get("post-type");

    const fetchPosts = React.useCallback(async () => {
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        try {
            const postsResponse = await axios.get(`${backendUrl}/post`, {
                headers,
                withCredentials: true,
            });

            if (postsResponse.status === 200) {
                const {
                    listingCount,
                    listingCampaignCount,
                    promotionCount,
                    promotionCampaignCount,
                    posts,
                } = postsResponse.data;
                setCounts({
                    listingCount,
                    listingCampaignCount,
                    promotionCount,
                    promotionCampaignCount,
                });


                setPosts(posts);
            } else {
                throw new Error("Failed to fetch posts");
            }
        } catch (error: unknown) {
            toast.error(
                <CustomToast
                    message="Failed to fetch posts! Please try again later."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, []);

    // Coalesce socket-driven refetches. Trailing edge so a burst of events
    // costs one request, plus a max wait so a CONTINUOUS stream (autopilot
    // populating a fleet emits post:created for minutes) still updates the
    // calendar progressively instead of deferring until the stream goes quiet.
    const debouncedFetchPosts = useCallback(() => {
        if (refetchTimeoutRef.current) {
            clearTimeout(refetchTimeoutRef.current);
        }

        const run = () => {
            if (refetchTimeoutRef.current) {
                clearTimeout(refetchTimeoutRef.current);
                refetchTimeoutRef.current = null;
            }
            if (maxWaitTimeoutRef.current) {
                clearTimeout(maxWaitTimeoutRef.current);
                maxWaitTimeoutRef.current = null;
            }
            fetchPosts();
        };

        refetchTimeoutRef.current = setTimeout(run, REFETCH_DEBOUNCE_MS);
        if (!maxWaitTimeoutRef.current) {
            maxWaitTimeoutRef.current = setTimeout(run, REFETCH_MAX_WAIT_MS);
        }
    }, [fetchPosts]);

    useEffect(
        () => () => {
            if (refetchTimeoutRef.current) {
                clearTimeout(refetchTimeoutRef.current);
            }
            if (maxWaitTimeoutRef.current) {
                clearTimeout(maxWaitTimeoutRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    // Listen for WebSocket events for real-time updates.
    //
    // Deliberately NOT gated on socket.connected: that is a mutable property, not
    // state, so bailing on it skips registration and nothing re-runs this effect
    // once the handshake finishes — the schedule then never updates until a
    // reload. socket.io keeps handlers across connect and reconnect, so attaching
    // to a not-yet-connected socket is safe.
    useEffect(() => {
        if (!socket) {
            return;
        }

        const handlePostDeleted = (eventData: any) => {
            // Check if it's a post deletion event
            if (eventData.resourceType === "post" && eventData.action === "deleted") {

                // Also remove the post locally for immediate UI update
                setPosts(currentPosts => {
                    const updatedPosts = currentPosts.filter(post => post.id !== eventData.resourceId);
                    return updatedPosts;
                });

                // Reconcile counts/server state. MB-1393: debounced like every
                // other handler — the optimistic removal above already gave the
                // user instant feedback, so an immediate refetch bought nothing
                // and made bulk deletes refetch once per post.
                debouncedFetchPosts();
            }
        };

        const handlePostUpdated = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "updated") {
                debouncedFetchPosts();
            }
        };

        // MB-1314: auto-video placeholders are created mid-render (post:created);
        // refetch so the "Processing" cards appear on the schedule while they generate.
        const handlePostCreated = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "created") {
                debouncedFetchPosts();
            }
        };

        const handleListingUpdated = (eventData: any) => {
            if (eventData.resourceType === "listing" && eventData.action === "updated") {
                debouncedFetchPosts();
            }
        };

        const handleListingDeleted = (eventData: any) => {
            if (eventData.resourceType === "listing" && eventData.action === "deleted") {
                debouncedFetchPosts();
            }
        };

        const handlePromotionUpdated = (eventData: any) => {
            if (eventData.resourceType === "promotion" && eventData.action === "updated") {
                debouncedFetchPosts();
            }
        };

        const handlePromotionDeleted = (eventData: any) => {
            if (eventData.resourceType === "promotion" && eventData.action === "deleted") {
                debouncedFetchPosts();
            }
        };

        // Listen to all events to see what's being received
        socket.on("post:deleted", handlePostDeleted);
        socket.on("post:updated", handlePostUpdated);
        socket.on("post:created", handlePostCreated);
        socket.on("listing:updated", handleListingUpdated);
        socket.on("listing:deleted", handleListingDeleted);
        socket.on("promotion:deleted", handlePromotionDeleted);
        socket.on("promotion:updated", handlePromotionUpdated);

        return () => {
            socket.off("post:deleted", handlePostDeleted);
            socket.off("post:updated", handlePostUpdated);
            socket.off("post:created", handlePostCreated);
            socket.off("listing:updated", handleListingUpdated);
            socket.off("listing:deleted", handleListingDeleted);
            socket.off("promotion:deleted", handlePromotionDeleted);
            socket.off("promotion:updated", handlePromotionUpdated);
        };
        // eslint-disable-next-line
    }, [socket, debouncedFetchPosts, isAuthenticated]);
    
    useEffect(() => {
        if (!loading && hasFetched && posts?.length === 0) {
            // MB-1373: the user opted out of the welcome modal — show the
            // (empty) schedule instead. Cookie expiry or cleared browser
            // data brings the modal back.
            if (welcomeOverlayPref.isHidden()) {
                return;
            }
            const {
                listingCount,
                listingCampaignCount,
                promotionCount,
                promotionCampaignCount,
            } = counts;

            if (listingCount === 0 && promotionCount === 0) {
                setOverlayType("firstListing");
                setOverlayVisible(true);
            } else if (
                listingCount > 0 &&
                listingCampaignCount === 0 &&
                promotionCount > 0 &&
                promotionCampaignCount === 0
            ) {
                setOverlayType("listingPromotionCampaign");
                setOverlayVisible(true);
            } else if (listingCount > 0 && listingCampaignCount === 0) {
                setOverlayType("listingCampaign");
                setOverlayVisible(true);
            } else if (promotionCount > 0 && promotionCampaignCount === 0) {
                setOverlayType("promotionCampaign");
                setOverlayVisible(true);
            }
        }
    }, [loading, hasFetched, posts, counts]);

    // MB-1393: FIRST LOAD ONLY. This used to also test `loading`, so every
    // socket-driven refetch swapped the whole screen for LoadingScreen and back
    // — the visible flash. Worse, it unmounted Calendar, destroying its local
    // state: the open DayView modal (Calendar's `selectedDay`), the selected
    // month/week and the scroll position all reset on every refetch. Keeping the
    // tree mounted lets the calendar update in place.
    if (!hasFetched) {
        return <LoadingScreen />;
    }

    return (
        <div className="flex flex-col items-center justify-center h-full w-full">
            {overlayVisible && overlayType ? (
                <WelcomeListing
                    welcome={welcome}
                    type={overlayType}
                    onClose={() => setOverlayVisible(false)}
                />
            ) : (
                <Calendar posts={posts} postType={postType} loading={loading} getAllPosts={fetchPosts} />
            )}
            <FloatingProcessingPosts />
        </div>
    );
};

export default HomeScreen;
